import React, { useContext } from 'react';

import { css } from '@emotion/react';
import styled from '@emotion/styled';

import { Button } from '../../components/Button';
import { PopupContactForm } from '../../components/PopupContactForm';
import { useFactorData, useStoreData, useStoryblokStrings } from '../../hooks';
import { CoreContext } from '../../providers';
import { dynamicTemplate } from '../../services/dynamicTemplate';
import { media } from '../../services/media';
import { themeStyles } from '../../services/themeStyles';
import { Storyblok, StoryblokSection } from '../../storyblok';
import { ScoreFactorsSlider } from './components/ScoreFactorsSlider';
import { ScoreFactorsContext } from './context';

export const ScoreFactors = ({
    sectionData,
}: StoryblokSection<Storyblok.Component.ScoreFactors>) => {
    const { scoreData } = useStoreData().user;
    const { openPopup, settings } = useContext(CoreContext);

    const { positiveFactors, negativeFactors } = useFactorData();

    const storyblokGeneralStrings = useStoryblokStrings('general-strings');
    const myScoreFactors = useStoryblokStrings('my-score-factors');

    if (!storyblokGeneralStrings || !myScoreFactors) {
        return null;
    }

    const positiveCount = positiveFactors.length;
    const negativeCount = negativeFactors.length;
    const globalTemplateVariables = { positiveCount, negativeCount };

    const positiveFactorsHeading = dynamicTemplate(
        myScoreFactors.positive_factors_headline,
        globalTemplateVariables,
    );
    const negativeFactorsHeading = dynamicTemplate(
        myScoreFactors.negative_factors_headline,
        globalTemplateVariables,
    );

    const {
        button_label_positive: buttonLabelPositive,
        button_label_negative: buttonLabelNegative,
        section_heading: sectionHeading,
        slider_heading_negative: sliderHeadingNegative,
        slider_heading_positive: sliderHeadingPositive,
        swipe_label: swipeLabel,
        tell_friend_button: tellFriendButton,
        component,
    } = sectionData;

    if (!buttonLabelPositive || !buttonLabelNegative || !sectionHeading) {
        return null;
    }

    return (
        <ScoreFactorsContext.Provider
            value={{
                scoreData,
                sectionData,
                positiveFactors,
                negativeFactors,
            }}
        >
            <Section data-block={component}>
                {sectionHeading && (
                    <Headline>{myScoreFactors.results_headline}</Headline>
                )}
                {positiveFactors.length > 0 && (
                    <ScoreFactorsSlider
                        headline={positiveFactorsHeading}
                        scoreFactors={positiveFactors}
                        swipeInfo
                    />
                )}
                {negativeFactors.length > 0 && (
                    <ScoreFactorsSlider
                        headline={negativeFactorsHeading}
                        scoreFactors={negativeFactors}
                        variant="orange"
                    />
                )}
                {tellFriendButton && (
                    <ButtonWrapper>
                        <Button
                            variant={
                                settings.languageVersion === 'pengestyrke'
                                    ? 'boxed'
                                    : 'primary'
                            }
                            onClick={() =>
                                openPopup(
                                    <PopupContactForm formType="tellFriend" />,
                                )
                            }
                        >
                            {tellFriendButton}
                        </Button>
                    </ButtonWrapper>
                )}
            </Section>
        </ScoreFactorsContext.Provider>
    );
};

const SectionUpscore = themeStyles(
    'upscore',
    css`
        ${media.max('md')} {
            margin-bottom: -1.6vw;
        }

        ${media.min('md')} {
            margin-bottom: -0.9vw;
        }

        /**
 * Swiper 7.4.1
 * Most modern mobile touch slider and framework with hardware accelerated transitions
 * https://swiperjs.com
 *
 * Copyright 2014-2021 Vladimir Kharlampidi
 *
 * Released under the MIT License
 *
 * Released on: December 24, 2021
 */

        @font-face {
            font-family: swiper-icons;
            src: url('data:application/font-woff;charset=utf-8;base64, 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');
            font-weight: 400;
            font-style: normal;
        }
        :root {
            --swiper-theme-color: #007aff;
        }
        .swiper {
            margin-left: auto;
            margin-right: auto;
            position: relative;
            overflow: hidden;
            list-style: none;
            padding: 0;
            z-index: 1;
        }
        .swiper-vertical > .swiper-wrapper {
            flex-direction: column;
        }
        .swiper-wrapper {
            position: relative;
            width: 100%;
            height: 100%;
            z-index: 1;
            display: flex;
            transition-property: transform;
            box-sizing: content-box;
        }
        .swiper-android .swiper-slide,
        .swiper-wrapper {
            transform: translate3d(0px, 0, 0);
        }
        .swiper-pointer-events {
            touch-action: pan-y;
        }
        .swiper-pointer-events.swiper-vertical {
            touch-action: pan-x;
        }
        .swiper-slide {
            flex-shrink: 0;
            width: 100%;
            height: 100%;
            position: relative;
            transition-property: transform;
        }
        .swiper-slide-invisible-blank {
            visibility: hidden;
        }
        .swiper-autoheight,
        .swiper-autoheight .swiper-slide {
            height: auto;
        }
        .swiper-autoheight .swiper-wrapper {
            align-items: flex-start;
            transition-property: transform, height;
        }
        .swiper-3d,
        .swiper-3d.swiper-css-mode .swiper-wrapper {
            perspective: 1200px;
        }
        .swiper-3d .swiper-cube-shadow,
        .swiper-3d .swiper-slide,
        .swiper-3d .swiper-slide-shadow,
        .swiper-3d .swiper-slide-shadow-bottom,
        .swiper-3d .swiper-slide-shadow-left,
        .swiper-3d .swiper-slide-shadow-right,
        .swiper-3d .swiper-slide-shadow-top,
        .swiper-3d .swiper-wrapper {
            transform-style: preserve-3d;
        }
        .swiper-3d .swiper-slide-shadow,
        .swiper-3d .swiper-slide-shadow-bottom,
        .swiper-3d .swiper-slide-shadow-left,
        .swiper-3d .swiper-slide-shadow-right,
        .swiper-3d .swiper-slide-shadow-top {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            height: 100%;
            pointer-events: none;
            z-index: 10;
        }
        .swiper-3d .swiper-slide-shadow {
            background: rgba(0, 0, 0, 0.15);
        }
        .swiper-3d .swiper-slide-shadow-left {
            background-image: linear-gradient(
                to left,
                rgba(0, 0, 0, 0.5),
                rgba(0, 0, 0, 0)
            );
        }
        .swiper-3d .swiper-slide-shadow-right {
            background-image: linear-gradient(
                to right,
                rgba(0, 0, 0, 0.5),
                rgba(0, 0, 0, 0)
            );
        }
        .swiper-3d .swiper-slide-shadow-top {
            background-image: linear-gradient(
                to top,
                rgba(0, 0, 0, 0.5),
                rgba(0, 0, 0, 0)
            );
        }
        .swiper-3d .swiper-slide-shadow-bottom {
            background-image: linear-gradient(
                to bottom,
                rgba(0, 0, 0, 0.5),
                rgba(0, 0, 0, 0)
            );
        }
        .swiper-css-mode > .swiper-wrapper {
            overflow: auto;
            scrollbar-width: none;
            -ms-overflow-style: none;
        }
        .swiper-css-mode > .swiper-wrapper::-webkit-scrollbar {
            display: none;
        }
        .swiper-css-mode > .swiper-wrapper > .swiper-slide {
            scroll-snap-align: start start;
        }
        .swiper-horizontal.swiper-css-mode > .swiper-wrapper {
            scroll-snap-type: x mandatory;
        }
        .swiper-vertical.swiper-css-mode > .swiper-wrapper {
            scroll-snap-type: y mandatory;
        }
        .swiper-centered > .swiper-wrapper::before {
            content: '';
            flex-shrink: 0;
            order: 9999;
        }
        .swiper-centered.swiper-horizontal
            > .swiper-wrapper
            > .swiper-slide:first-child {
            margin-inline-start: var(--swiper-centered-offset-before);
        }
        .swiper-centered.swiper-horizontal > .swiper-wrapper::before {
            height: 100%;
            min-height: 1px;
            width: var(--swiper-centered-offset-after);
        }
        .swiper-centered.swiper-vertical
            > .swiper-wrapper
            > .swiper-slide:first-child {
            margin-block-start: var(--swiper-centered-offset-before);
        }
        .swiper-centered.swiper-vertical > .swiper-wrapper::before {
            width: 100%;
            min-width: 1px;
            height: var(--swiper-centered-offset-after);
        }
        .swiper-centered > .swiper-wrapper > .swiper-slide {
            scroll-snap-align: center center;
        }
    `,
);

const SectionPengeStyrke = themeStyles(
    'pengestyrke',
    css`
        max-width: 1200px;
        margin: 20px auto;
        padding: 0 15px;

        ${media.min('md')} {
            margin: 40px auto;
        }
    `,
);

const Section = styled.section`
    overflow: hidden;
    ${SectionUpscore}
    ${SectionPengeStyrke}
`;

const Headline = styled.h2`
    text-align: center;
`;

const ButtonWrapper = styled.div`
    display: flex;
    justify-content: center;
    padding: 30px 0;
`;
